import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { monthIndexToTranslationKey } from "../../shared/monthIndexToTranslationKey";
import { CalendarIcon } from "lucide-react";

type DateOption = {
  value: string,
  readableValue: string
}

type Props = {
  values?: DateOption[],
  onSelect?: (e: any) => void,
  value?: string,
  compact?: boolean,
  placeholder?: string
};

// TODO: reuse SelectInput with custom CSS?
function DropSelect({ values = [], onSelect, value = '', compact = false, placeholder }: Props) {

  const { t } = useTranslation();
  const [options, setOptions] = useState<DateOption[]>([]);
  const placeholderText = placeholder || t("global.month_picker");

  useEffect(() => {
    const today = new Date();
    const monthYears: DateOption[] = [];
    for (let i = today.getFullYear(); i <= today.getFullYear() + 1; i++) {
      for (let j = 0; j < 12; j++) {
        const date = new Date(i, j, 3);
        monthYears.push({ value: date.toISOString().split('T')[0], readableValue: `${t(monthIndexToTranslationKey(date.getMonth()))} ${date.getFullYear()}`});
      }
    }
    setOptions(monthYears);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (compact) {
    return (
      <div className="inline-block">
        {/* Mobile: compact */}
        <div className="md:hidden">
          <div className="relative inline-block" title={placeholderText!}>
            <button
              type="button"
              className="inline-flex items-center justify-center rounded border border-gray-200 bg-card text-foreground hover:bg-muted focus:outline-none focus:ring-2 focus:ring-gold-300 py-1 px-2 h-8"
            >
              <CalendarIcon className="w-4 h-4 text-foreground" />
            </button>
            <select
              required
              className="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
              onChange={onSelect}
              value={value}
              data-dd-action-name={'Dropdown Month Select'}
            >
              <option hidden value="">{placeholderText}</option>
              <option value="">{ t("orders.filters.month.all") }</option>
              { options.map((m, i) => 
                <option
                  value={m.value}
                  key={i}
                  data-dd-action-name={'Dropdown Month Select Option'}
                  className="font-sans text-sm text-foreground"
                >
                  { m.readableValue }
                </option>
              )}
            </select>
          </div>
        </div>
        {/* Desktop: full select */}
        <select
          required
          className="hidden md:inline-block font-sans text-sm text-foreground rounded border border-gray-200 bg-card hover:bg-muted px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gold-300 focus:border-gold-300 transition-colors duration-200 appearance-none"
          onChange={onSelect}
          value={value}
          data-dd-action-name={'Dropdown Month Select'}
        >
          <option hidden value="">{placeholderText}</option>
          <option value="">{ t("orders.filters.month.all") }</option>
          { options.map((m, i) => 
            <option
              value={m.value}
              key={i}
              data-dd-action-name={'Dropdown Month Select Option'}
              className="font-sans text-sm text-foreground"
            >
              { m.readableValue }
            </option>
          )}
        </select>
      </div>
    );
  }

  return (
    <select
      required
      className="font-sans text-sm text-foreground rounded border border-gray-200 bg-card hover:bg-muted px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gold-300 focus:border-gold-300 transition-colors duration-200 appearance-none"
      onChange={onSelect}
      value={value}
      data-dd-action-name={'Dropdown Month Select'}
    >
      <option hidden value="">{placeholderText}</option>
      <option value="">{ t("orders.filters.month.all") }</option>
      { options.map((m, i) => 
        <option
          value={m.value}
          key={i}
          data-dd-action-name={'Dropdown Month Select Option'}
          className="font-sans text-sm text-foreground"
        >
          { m.readableValue }
        </option>
      )}
    </select>
  );
}

export default DropSelect;
